/**
 * Client-side image resize for upload.
 *
 * Phone photos routinely weigh 2-5 MB; base64-encoding inflates them
 * by ~33% on top; nginx then 413s on anything above `client_max_body_size`
 * (devapi's default was 1 MB before PR 5's ops task). Resizing to a
 * modest canvas before the request keeps us well under any reasonable
 * body limit, cuts upload time on 2G/3G, and loses nothing for a crop
 * diagnosis (a leaf lesion is just as visible at 1280 px).
 *
 * The resizer:
 *   - keeps aspect ratio
 *   - caps the longest edge at `maxEdge` (default 1280 px)
 *   - re-encodes as JPEG at `quality` (default 0.8)
 *   - returns a `data:image/jpeg;base64,…` string suitable for the
 *     diagnosis payload
 *
 * No-ops when the input is already smaller than `maxEdge` on both axes
 * — re-encoding would just lose quality for nothing.
 */
export async function resizeImageForUpload(
  file,
  { maxEdge = 1280, quality = 0.8 } = {},
) {
  if (!(file instanceof Blob)) {
    throw new TypeError('resizeImageForUpload expects a File / Blob');
  }

  const dataUrl = await _fileToDataUrl(file);
  const img = await _loadImage(dataUrl);

  // If the image is already smaller than the cap on both axes, keep it
  // as-is — avoids a lossy re-encode of a tiny screenshot or already
  // compressed thumbnail.
  if (img.naturalWidth <= maxEdge && img.naturalHeight <= maxEdge) {
    return dataUrl;
  }

  const scale = maxEdge / Math.max(img.naturalWidth, img.naturalHeight);
  const targetWidth = Math.round(img.naturalWidth * scale);
  const targetHeight = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  // JPEG so phone photos (which are JPEG anyway) stay small; PNG
  // alternatives balloon by ~5x for photographic content.
  return canvas.toDataURL('image/jpeg', quality);
}

/** Return just the base64 payload (strips the `data:…;base64,` prefix). */
export function extractBase64Payload(dataUrl) {
  if (typeof dataUrl !== 'string') return '';
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/**
 * Center-cropped square resize for profile avatars.
 *
 * A profile photo is always rendered inside a circle at a few fixed
 * sizes (10 px in the header, ~96 px on the Profile page), so a
 * ~400 px square JPEG at quality 0.85 is the sweet spot — plenty of
 * detail for 2× DPI retina displays, trivially small (~30 KB base64)
 * so the user row stays comfortably under any body-size limit, and
 * naturally guarantees the client never ships a 4-megapixel portrait.
 *
 * Returns a `data:image/jpeg;base64,…` URL ready to drop into
 * `UserUpdate.avatar_url`.
 */
export async function resizeImageForAvatar(file, { edge = 400, quality = 0.85 } = {}) {
  if (!(file instanceof Blob)) {
    throw new TypeError('resizeImageForAvatar expects a File / Blob');
  }

  const dataUrl = await _fileToDataUrl(file);
  const img = await _loadImage(dataUrl);

  const srcEdge = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - srcEdge) / 2;
  const sy = (img.naturalHeight - srcEdge) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = edge;
  canvas.height = edge;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  // Center-crop: pull the largest square from the middle of the source
  // and scale it down to `edge` pixels. Any landscape / portrait photo
  // ends up as a square the UI can confidently clip into a circle.
  ctx.drawImage(img, sx, sy, srcEdge, srcEdge, 0, 0, edge, edge);

  return canvas.toDataURL('image/jpeg', quality);
}

function _fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function _loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = src;
  });
}
